import { DollarSign, Edit2, Plus, Trash2 } from 'lucide-react';
import { Transaction } from '../lib/types';
import { cn, formatCurrency } from '../lib/utils';

interface IncomeTabProps {
  summary: { totalIncome: number; totalExpenses: number; balance: number };
  localCurrency: string;
  groupedIncome: Transaction[];
  onAddTransaction: () => void;
  onEditTransaction: (t: Transaction) => void;
  onDeleteTransaction: (id: string) => void;
  isPrivacyMode: boolean;
}

export function IncomeTab({ summary, localCurrency, groupedIncome, onAddTransaction, onEditTransaction, onDeleteTransaction, isPrivacyMode }: IncomeTabProps) {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-bold text-lg text-zinc-900 dark:text-zinc-100">Income Ledger</h3>
        <button onClick={onAddTransaction} className="w-10 h-10 bg-zinc-900 text-white rounded-xl flex items-center justify-center shadow-lg">
          <Plus className="w-5 h-5" />
        </button>
      </div>

      <div className="bg-zinc-900 dark:bg-zinc-800 text-white p-6 rounded-[32px] shadow-xl shadow-zinc-200 dark:shadow-zinc-900/50 space-y-5 transition-colors">
        <div className="flex justify-between items-center border-b border-white/5 pb-4">
          <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">Total Income</p>
          <p className={cn("text-xl font-bold text-emerald-400", isPrivacyMode && "blur-sm select-none")}>{formatCurrency(summary.totalIncome, localCurrency)}</p>
        </div>
        <div className="flex justify-between items-center border-b border-white/5 pb-4">
          <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">Total Expenses</p>
          <p className={cn("text-xl font-bold text-rose-400", isPrivacyMode && "blur-sm select-none")}>{formatCurrency(summary.totalExpenses, localCurrency)}</p>
        </div>
        <div className="flex justify-between items-center pt-2">
          <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">Net Balance</p>
          <p className={cn('text-2xl font-bold', summary.balance < 0 ? 'text-rose-400' : 'text-emerald-400', isPrivacyMode && "blur-sm select-none")}>{formatCurrency(summary.balance, localCurrency)}</p>
        </div>
      </div>

      <div className="space-y-4">
        {groupedIncome.map((inc) => (
          <div key={inc.id} className="bg-white dark:bg-zinc-900 p-4 rounded-2xl border border-zinc-100 dark:border-zinc-800 shadow-sm flex items-center group transition-all hover:border-emerald-100 dark:hover:border-emerald-900/50">
            <div className="w-10 h-10 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl flex items-center justify-center shrink-0">
              <DollarSign className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div className="flex-1 ml-3">
              <div className="flex justify-between items-center">
                <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">{inc.note || 'Untitled'}</p>
                <p className={cn("text-sm font-bold text-emerald-600", isPrivacyMode && "blur-sm select-none")}>{formatCurrency(inc.amount, localCurrency)}</p>
              </div>
              <div className="flex justify-between items-center mt-1">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-zinc-400 font-medium">{inc.date}</span>
                  <span className="w-1 h-1 bg-zinc-200 rounded-full" />
                  <span className="text-[10px] text-zinc-500 font-medium uppercase tracking-wide">{inc.category}</span>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={(e) => { e.stopPropagation(); onEditTransaction(inc); }} className="p-2 text-zinc-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"><Edit2 className="w-4 h-4" /></button>
                  {!inc.id.startsWith('default-') && (
                    <button onClick={(e) => { e.stopPropagation(); onDeleteTransaction(inc.id); }} className="p-2 text-zinc-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"><Trash2 className="w-4 h-4" /></button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
